/**
 * Toaster custom — alternative à `window.alert()` pour les notifications
 * éphémères (success / error / info).
 *
 * Usage :
 *   const toast = useToast();
 *   toast.success("Invitation renvoyée.");
 *   toast.error("Action refusée : ...");
 *
 * Empilement vertical en haut à droite, auto-dismiss 4s, click pour fermer.
 * Volontairement minimaliste : pas de queue avancée, pas d'undo, pas d'icônes
 * personnalisées par toast — juste de quoi remplacer `window.alert` proprement.
 */

import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

type ToastVariant = "success" | "error" | "info";

interface Toast {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Compteur monotone : garantit l'unicité des id sans dépendre de l'horloge
  // (Date.now() peut collisionner si deux push arrivent dans le même ms,
  // p. ex. via Promise.all + .map sur les erreurs d'un batch).
  const nextIdRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((variant: ToastVariant, message: string) => {
    nextIdRef.current += 1;
    const id = nextIdRef.current;
    setToasts((prev) => [...prev, { id, variant, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, AUTO_DISMISS_MS);
  }, []);

  // Memoïsé : ce provider est monté au-dessus de <App/> et 87 fichiers appellent
  // `useToast()`. Sans `useMemo`, chaque toast (apparition PUIS auto-dismiss 4 s
  // plus tard) recréait la valeur du contexte et re-rendait tous les
  // consommateurs — un toast de succès après un ajustement de stock re-rendait
  // deux fois la liste sous-jacente. `push` est déjà stable (`useCallback` []).
  const value: ToastContextValue = useMemo(
    () => ({
      success: (m: string) => push("success", m),
      error: (m: string) => push("error", m),
      info: (m: string) => push("info", m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2"
          role="region"
          aria-label="Notifications"
        >
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onClose={() => remove(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  // Petite animation d'entrée (fade + slide) — on monte en off-screen puis on
  // bascule en visible au prochain tick. Pas besoin d'animations CSS keyframe.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const styles: Record<ToastVariant, { ring: string; bg: string; icon: ReactNode; title: string }> =
    {
      success: {
        ring: "ring-vine-200",
        bg: "bg-vine-50",
        icon: <CheckCircle2 size={20} className="text-vine-600" />,
        title: "Succès",
      },
      error: {
        ring: "ring-rust-200",
        bg: "bg-rust-50",
        icon: <AlertCircle size={20} className="text-rust-600" />,
        title: "Erreur",
      },
      info: {
        ring: "ring-champagne-200",
        bg: "bg-champagne-50",
        icon: <Info size={20} className="text-champagne-700" />,
        title: "Info",
      },
    };
  const s = styles[toast.variant];

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 rounded-lg ${s.bg} p-3 shadow-lg ring-1 ${s.ring} transition-all duration-200 ${
        visible ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0"
      }`}
    >
      <div className="mt-0.5 shrink-0">{s.icon}</div>
      <div className="flex-1 text-sm text-aubergine-900">{toast.message}</div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="shrink-0 rounded p-0.5 text-neutral-400 hover:bg-black/5 hover:text-aubergine-900"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() doit être utilisé sous <ToastProvider>");
  return ctx;
}
